import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import InitialLoader from "./components/loadingAndErrorPages/InitialLoader";
import UpdateBanner from "./versionUpdateBanner/UpdateBanner";

import { Toaster } from "sonner";

import { PublicLayout } from "./layouts/PublicLayout";
import { ExpertPrivateLayout } from "./layouts/ExpertPrivateLayout";
import { UserPrivateLayout } from "./layouts/UserPrivateLayout";

import AdminLogin from "./admin/login/AdminLogin"; // Adjust path if needed

import ScrollToTopDefault from "./ui/ScrollToTopDefault";
import { ScrollToTopButton } from "./ui/ScrollToTopButton";

import HomePage from "./components/homePage/Home";
import CategoryMainPage from "./components/categories/CategoryMainPage";
import AstrologyMainPage from "./components/homePage/dedicatedAstrologyPage/AstrologyMainPage";

import ExpertOnboarding from "./components/authPages/expertAuth/onboardingsteps/ExpertOnboarding";
import OnboardingTracker from "./components/authPages/expertAuth/onboardingsteps/OnboardingTracker";

// import AllBlogsPage from "./components/blogs/AllBlogsPage";
// import BlogDetailPage from "./components/blogs/BlogDetailPage";

// 🟢 IMPORT THE CONNECTIVITY ROOMS & GLOBAL WRAPPER
import { GlobalChatWrapper } from "./layouts/GlobalChatWrapper";

// 🟢 IMPORT LIVE STREAM COMPONENTS
import { LiveStreamWrapper } from "./components/liveStream/LiveStreamWrapper";

import Experts from "./components/experts/Experts";
import Astrology from "./components/experts/AstrologyExperts/Astrology";
import Doctor from "./components/experts/DoctorExperts/Doctor";
import Lawyer from "./components/experts/LawyerExperts/Lawyer";

import ExpertWrapper from "./dashboards/expertDashboard/ExpertWrapper";

const ExpertProfile = lazy(() => import("./components/experts/ExpertProfile"));

const ExpertSignup = lazy(
  () => import("./components/authPages/expertAuth/Expert-SignUp"),
);

const ExpertLogin = lazy(
  () => import("./components/authPages/expertAuth/Expert-Login"),
);

const OnboardingSuccess = lazy(() =>
  import("./components/authPages/expertAuth/onboardingsteps/OnboardingSuccess").then(
    (m) => ({
      default: m.OnboardingSuccess,
    }),
  ),
);

const UserSignup = lazy(() =>
  import("./components/authPages/userAuth/User-SignUp").then((m) => ({
    default: m.UserSignup,
  })),
);

const UserLogin = lazy(
  () => import("./components/authPages/userAuth/User-Login"),
);

const ForgotPassword = lazy(
  () => import("./components/authPages/ForgotPassword"),
);

const UserDashboardHome = lazy(
  () => import("./dashboards/userDashboard/UserDashboard"),
);

const UserMessagesPage = lazy(
  () => import("./dashboards/userDashboard/UserMessagesPage"),
);

const UserOrders = lazy(
  () => import("./dashboards/userDashboard/UserOrders"),
);

const UserWalletPage = lazy(
  () => import("./components/payments/userWalletRecharge/UserWalletPage"),
);

const RechargeSuccessPage = lazy(
  () => import("./components/payments/RechargeSuccessPage"),
);

const RechargePendingPage = lazy(
  () => import("./components/payments/RechargePendingPage"),
);

const RechargeFailedPage = lazy(
  () => import("./components/payments/RechargeFailedPage"),
);

const UserFollowing = lazy(
  () => import("./dashboards/userDashboard/UserFollowing"),
);

const UserPrescriptions = lazy(
  () => import("./dashboards/userDashboard/UserPrescriptions"),
);

const UserProfile = lazy(
  () => import("./dashboards/userDashboard/UserProfile"),
);

const AllBlogsPage = lazy(
  () => import("./components/blogs/AllBlogsPage"),
);

const BlogDetailPage = lazy(
  () => import("./components/blogs/BlogDetailPage"),
);

const ChatRoom = lazy(
  () => import("./connectivity/chat/chatLayout/ChatRoom"),
);

const CallRoom = lazy(
  () => import("./connectivity/call/CallRoom"),
);

const VideoRoom = lazy(
  () => import("./connectivity/videoCall/VideoRoom"),
);

const UserLiveStreamContainer = lazy(() =>
  import("./components/liveStream/UserLiveStreamContainer").then((m) => ({
    default: m.UserLiveStreamContainer,
  })),
);

const LiveSessionsLobby = lazy(() =>
  import("./components/liveStream/LiveSessionsLobby").then((m) => ({
    default: m.LiveSessionsLobby,
  })),
);

const SubCategoryWrapper = lazy(
  () => import("./components/subCategories/SubCategoryWrapper"),
);

// 🟢 NEW LAZY LOADED ADMIN WRAPPER
const AdminWrapper = lazy(() => import("./admin/dashboard/AdminWrapper"));

const Error404 = lazy(
  () => import("./components/loadingAndErrorPages/Error404"),
);

const AboutPage = lazy(
  () => import("./components/about/AboutPage"),
);

const ContactPage = lazy(
  () => import("./components/footer/ContactPage"),
);

const TermsAndConditions = lazy(
  () => import("./components/footer/TermsAndConditions"),
);

const PrivacyPolicy = lazy(
  () => import("./components/footer/PrivacyPolicy"),
);

const RefundPolicy = lazy(
  () => import("./components/footer/RefundPolicy"),
);

const Faq = lazy(
  () => import("./components/footer/Faq"),
);

const Career = lazy(
  () => import("./components/footer/carrerPages/CareersPage"),
);

const JobApplicationPage = lazy(
  () => import("./components/footer/carrerPages/JobApplicationPage"),
);

export default function App() {
  return (
    <BrowserRouter>
      <UpdateBanner />

      {/* Global scroll behavior & Toasts */}
      <Toaster
        position="top-right"
        richColors
        closeButton
        duration={3000}
      />

      <ScrollToTopDefault />
      <ScrollToTopButton />

      {/* Lazy loading boundary */}
      <Suspense fallback={<InitialLoader />}>
        <Routes>
          {/* 🛡️ ADMIN PUBLIC ROUTE (Not lazy-loaded so it snaps open instantly) */}
          <Route path="/admin/login" element={<AdminLogin />} />

          {/* 🔒 ADMIN SECURE ROUTES (Lazy loads the ENTIRE admin ecosystem at once) */}
          <Route path="/admin/*" element={<AdminWrapper />} />

          {/* 🌐 1. Public Pages (Accessible by anyone - NO SOCKETS HERE) */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />

            <Route path="/categories" element={<CategoryMainPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/careers" element={<Career />} />
            <Route path="/astro" element={<AstrologyMainPage />} />

            <Route
              path="/careers/apply/:jobId"
              element={<JobApplicationPage />}
            />

            {/* 🟢 The /:category? allows tabs to act as individual pages */}
            <Route
              path="/live-sessions/:category?"
              element={<LiveSessionsLobby />}
            />

            <Route path="/experts" element={<Experts />} />
            <Route path="/experts/astrology" element={<Astrology />} />
            <Route path="/experts/doctor" element={<Doctor />} />
            <Route path="/experts/lawyer" element={<Lawyer />} />

            {/* 🟢 SUBCATEGORY ROUTES */}
            <Route
              path="/experts/astrology/:subCategorySlug"
              element={<SubCategoryWrapper />}
            />

            <Route
              path="/experts/lawyer/:subCategorySlug"
              element={<SubCategoryWrapper />}
            />

            <Route
              path="/experts/doctor/:subCategorySlug"
              element={<SubCategoryWrapper />}
            />

            <Route
              path="/expert/:expertCode"
              element={<ExpertProfile />}
            />

            {/* ✅ Signups and Logins are completely separate from the Private Layouts */}
            <Route
              path="/expert-signup"
              element={<ExpertSignup />}
            />

            <Route
              path="/expert-login"
              element={<ExpertLogin />}
            />

            <Route
              path="/user-signup"
              element={<UserSignup />}
            />

            <Route
              path="/user-login"
              element={<UserLogin />}
            />

            <Route
              path="/forgot-password"
              element={<ForgotPassword />}
            />

            {/* ✅ 404 page (with Navbar + Footer) */}
            <Route path="*" element={<Error404 />} />
          </Route>

          {/* 🟢 PRIVATE APPLICATION LIFECYCLE */}
          {/* 
              GlobalChatWrapper stays mounted while navigating between:
              - User dashboard
              - Consultation rooms
              - Expert dashboard
              - Live consultation routes

              This prevents the persistent socket provider from being
              destroyed when a consultation is minimized.
          */}
          <Route element={<GlobalChatWrapper />}>

            {/* 🟢 2. USER DASHBOARD ROUTE (Wrapped securely in UserPrivateLayout) */}
            {/* these are controlled directly by backend directly */}
            <Route path="/blogs" element={<AllBlogsPage />} />
            <Route path="/blog/:id" element={<BlogDetailPage />} />

            <Route element={<UserPrivateLayout />}>
              <Route
                path="/user/dashboard"
                element={<UserDashboardHome />}
              />

              <Route
                path="/user/messages"
                element={<UserMessagesPage />}
              />

              <Route
                path="/user/prescriptions"
                element={<UserPrescriptions />}
              />

              <Route
                path="/user/profile"
                element={<UserProfile />}
              />

              <Route
                path="/user/followings"
                element={<UserFollowing />}
              />

              {/* 🟢 ADD THE NEW ROUTE HERE */}
              <Route
                path="/user/orders"
                element={<UserOrders />}
              />

              <Route
                path="/user/wallet"
                element={<UserWalletPage />}
              />
            </Route>

            <Route
              path="/user/wallet/recharge-success"
              element={<RechargeSuccessPage />}
            />
            <Route
  path="/user/wallet/recharge-pending"
  element={<RechargePendingPage />}
/>

            <Route
              path="/user/wallet/recharge-failed"
              element={<RechargeFailedPage />}
            />

            {/* 🟢 FULL-SCREEN CONNECTIVITY ROUTES (No Navbars/Footers) */}
            <Route
              path="/chat/:expertCode"
              element={<ChatRoom />}
            />

            <Route
              path="/call/:expertCode"
              element={<CallRoom />}
            />

            <Route
              path="/video/:expertCode"
              element={<VideoRoom />}
            />

            {/* 🟢 USER LIVE STREAM ROUTE: Join a stream */}
            <Route
              path="/user/live/:channelId"
              element={
                <LiveStreamWrapper>
                  <UserLiveStreamContainer />
                </LiveStreamWrapper>
              }
            />

            {/* 🔒 4. Private Pages (Requires Expert Login) */}
            <Route element={<ExpertPrivateLayout />}>

              {/* Onboarding steps (Protected, but separate from Dashboard) */}
              <Route
                path="/expert/onboarding-tracker"
                element={<OnboardingTracker />}
              />

              <Route
                path="/expert-onboarding/:category"
                element={<ExpertOnboarding />}
              />

              <Route
                path="/expert/application-submitted"
                element={<OnboardingSuccess />}
              />

              {/* 🟢 DASHBOARD WRAPPER (Handles /expert-dashboard/* routes) */}
              <Route
                path="/expert-dashboard/*"
                element={<ExpertWrapper />}
              />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}